import { CoreError, CoreErrorCause } from "../../../domain/models/CoreError";

/**
 * Partnership-specific error causes
 */
export enum PartnershipErrorCause {
  PartnershipNotFound = "partnership_not_found",
  InvalidPartnershipData = "invalid_partnership_data",
  InvalidInvitationCode = "invalid_invitation_code",
  InvitationCodeAlreadyUsed = "invitation_code_already_used",
  PartnerNotFound = "partner_not_found",
  PartnershipAlreadyExists = "partnership_already_exists",
  NoSelectedPartnership = "no_selected_partnership",
  InvalidPartnershipStatus = "invalid_partnership_status",
  PermissionDenied = "permission_denied",
  NetworkError = "network_error",
}

/**
 * Converts Firebase errors and partnership-specific errors to CoreError domain model.
 */
export class PartnershipErrorConverter {
  /**
   * Converts Firebase Database errors to CoreError with partnership-specific causes.
   */
  static domainModel(error: unknown): CoreError {
    if (error instanceof CoreError) {
      return error;
    }

    // Firebase Database error codes
    const err = (error ?? {}) as { code?: string; message?: string };
    const code = err.code || "";
    const message = err.message || "Unknown error";
    const normalizedMessage = String(message).toLowerCase();

    if (
      normalizedMessage.includes("permission") &&
      normalizedMessage.includes("denied")
    ) {
      return new CoreError({
        cause: CoreErrorCause.PermissionDenied,
        context: `Permission denied: ${message}`,
        originalError: error,
      });
    }

    switch (code) {
      case "DATABASE/PERMISSION-DENIED":
      case "PERMISSION_DENIED":
        return new CoreError({
          cause: CoreErrorCause.PermissionDenied,
          context: `Permission denied: ${message}`,
          originalError: error,
        });

      case "DATABASE/NETWORK-ERROR":
      case "NETWORK_ERROR":
        return new CoreError({
          cause: CoreErrorCause.NetworkError,
          context: `Network error: ${message}`,
          originalError: error,
        });

      case "DATABASE/UNAVAILABLE":
        return new CoreError({
          cause: CoreErrorCause.NetworkError,
          context: `Database unavailable: ${message}`,
          originalError: error,
        });

      case "DATABASE/DATA-STALE":
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: `Stale data: ${message}`,
          originalError: error,
        });

      case "DATABASE/OPERATION-FAILED":
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: `Operation failed: ${message}`,
          originalError: error,
        });

      default:
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: message,
          originalError: error,
        });
    }
  }

  /**
   * Creates a partnership-specific error.
   */
  static createPartnershipError(
    cause: PartnershipErrorCause,
    context: string,
  ): CoreError {
    let coreErrorCause: CoreErrorCause;

    switch (cause) {
      case PartnershipErrorCause.PartnershipNotFound:
      case PartnershipErrorCause.PartnerNotFound:
        coreErrorCause = CoreErrorCause.DataNotFound;
        break;

      case PartnershipErrorCause.InvalidPartnershipData:
      case PartnershipErrorCause.InvalidInvitationCode:
      case PartnershipErrorCause.InvalidPartnershipStatus:
        coreErrorCause = CoreErrorCause.DataNotFound;
        break;

      case PartnershipErrorCause.InvitationCodeAlreadyUsed:
      case PartnershipErrorCause.PartnershipAlreadyExists:
        coreErrorCause = CoreErrorCause.Forbidden;
        break;

      case PartnershipErrorCause.NoSelectedPartnership:
        coreErrorCause = CoreErrorCause.DataNotFound;
        break;

      case PartnershipErrorCause.PermissionDenied:
        coreErrorCause = CoreErrorCause.PermissionDenied;
        break;

      case PartnershipErrorCause.NetworkError:
        coreErrorCause = CoreErrorCause.NetworkError;
        break;

      default:
        coreErrorCause = CoreErrorCause.Unknown;
    }

    return new CoreError({
      cause: coreErrorCause,
      context,
    });
  }
}
